import {
  CalendarEvent,
  ICalendarEvent,
  ICalendarEventJS,
} from '@app/models/CalendarEvent'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { List, Record } from 'immutable'
import moment from 'moment'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export enum DueDiligenceCalendarScope {
  All = 'All',
  Mine = 'Mine',
}

export interface IDueDiligenceCalendarState {
  readonly downloadUrl: string
  readonly endTime: number
  readonly error?: string
  readonly events: List<ICalendarEvent>
  readonly isLoading: boolean
  readonly scope: DueDiligenceCalendarScope
  readonly startTime: number
}

const DueDiligenceCalendarStateRecord = Record({
  downloadUrl: '',
  endTime: 0,
  error: undefined,
  events: List<CalendarEvent>(),
  isLoading: false,
  scope: DueDiligenceCalendarScope.Mine,
  startTime: 0,
})

export class DueDiligenceCalendarState extends DueDiligenceCalendarStateRecord
  implements IDueDiligenceCalendarState {
  public static fromJS(initialState?: Partial<IDueDiligenceCalendarState>) {
    if (!initialState) {
      return new DueDiligenceCalendarState()
    }

    const { events, ...rest } = initialState

    return new DueDiligenceCalendarState({
      events: events
        ? List(events.map((e) => new CalendarEvent(e)))
        : List<CalendarEvent>(),
      ...rest,
    })
  }

  public readonly downloadUrl: string
  public readonly endTime: number
  public readonly error?: string
  public readonly events: List<CalendarEvent>
  public readonly isLoading: boolean
  public readonly scope: DueDiligenceCalendarScope
  public readonly startTime: number

  constructor(props?: Partial<DueDiligenceCalendarState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum DueDiligenceCalendarActionTypes {
  LoadEvents = 'DUE_DILIGENCE_CALENDAR::LOAD_EVENTS',
  LoadEventsFailure = 'DUE_DILIGENCE_CALENDAR::LOAD_EVENTS_FAILURE',
  LoadEventsRequest = 'DUE_DILIGENCE_CALENDAR::LOAD_EVENTS_REQUEST',
  LoadEventsSuccess = 'DUE_DILIGENCE_CALENDAR::LOAD_EVENTS_SUCCESS',
  SetScope = 'DUE_DILIGENCE_CALENDAR::SET_SCOPE',
}

interface ILoadEventsAction {
  end: number
  start: number
  type: DueDiligenceCalendarActionTypes.LoadEvents
}

interface ILoadEventsFailureAction {
  error: string
  type: DueDiligenceCalendarActionTypes.LoadEventsFailure
}

interface ILoadEventsRequestAction {
  end: number
  start: number
  type: DueDiligenceCalendarActionTypes.LoadEventsRequest
}

interface ILoadEventsSuccessAction {
  downloadUrl: string
  events: CalendarEvent[]
  type: DueDiligenceCalendarActionTypes.LoadEventsSuccess
}

interface ISetScopeAction {
  scope: DueDiligenceCalendarScope
  type: DueDiligenceCalendarActionTypes.SetScope
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadEventsFailureAction
  | ILoadEventsRequestAction
  | ILoadEventsSuccessAction
  | ISetScopeAction

// -------------------------
// ACTION CREATORS
// -------------------------

function loadEvents(start: number, end: number): ILoadEventsAction {
  return {
    end,
    start,
    type: DueDiligenceCalendarActionTypes.LoadEvents,
  }
}

function loadEventsFailure(error: string): ILoadEventsFailureAction {
  return {
    error,
    type: DueDiligenceCalendarActionTypes.LoadEventsFailure,
  }
}

function loadEventsRequest(
  start: number,
  end: number,
): ILoadEventsRequestAction {
  return {
    end,
    start,
    type: DueDiligenceCalendarActionTypes.LoadEventsRequest,
  }
}

function loadEventsSuccess(
  events: CalendarEvent[],
  downloadUrl: string,
): ILoadEventsSuccessAction {
  return {
    downloadUrl,
    events,
    type: DueDiligenceCalendarActionTypes.LoadEventsSuccess,
  }
}

function setScope(scope: DueDiligenceCalendarScope): ISetScopeAction {
  return {
    scope,
    type: DueDiligenceCalendarActionTypes.SetScope,
  }
}

export const actionCreators = {
  loadEvents,
  setScope,
}

// -------------------------
// REDUCER
// -------------------------
const initState = new DueDiligenceCalendarState()

export const reducer: Reducer<DueDiligenceCalendarState> = (
  state: DueDiligenceCalendarState = initState,
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case DueDiligenceCalendarActionTypes.LoadEventsFailure:
      return state
        .set('error', undefined)
        .set('isLoading', false) as DueDiligenceCalendarState

    case DueDiligenceCalendarActionTypes.LoadEventsRequest:
      return state
        .set('startTime', action.start)
        .set('endTime', action.end)
        .set('isLoading', true) as DueDiligenceCalendarState

    case DueDiligenceCalendarActionTypes.LoadEventsSuccess:
      return state
        .set('downloadUrl', action.downloadUrl)
        .set('events', List(action.events))
        .set('isLoading', false) as DueDiligenceCalendarState

    case DueDiligenceCalendarActionTypes.SetScope:
      return state.set('scope', action.scope) as DueDiligenceCalendarState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadEventsSaga(action: ILoadEventsAction) {
  try {
    yield put(loadEventsRequest(action.start, action.end))

    const scope: DueDiligenceCalendarScope = yield select(selectScope())

    const query = [
      `start=${encodeURIComponent(moment.unix(action.start).toISOString())}`,
      `end=${encodeURIComponent(moment.unix(action.end).toISOString())}`,
    ]

    const baseUrl =
      scope === DueDiligenceCalendarScope.All
        ? '/api/Calendars/DueDiligence'
        : '/api/Calendars/DueDiligence/My'

    const result = yield makeAuthenticatedRequest(
      `${baseUrl}?${query.join('&')}`,
    )

    if (result.success === true) {
      const events = result.results.map((c: ICalendarEventJS) =>
        CalendarEvent.fromJS(c),
      )
      yield put(loadEventsSuccess(events, result.downloadUrl))
    } else {
      yield put(loadEventsFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadEventsFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadEvents() {
  yield takeLatest(DueDiligenceCalendarActionTypes.LoadEvents, loadEventsSaga)
}

export function* dueDiligenceCalendarRootSaga() {
  yield fork(watchLoadEvents)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState) =>
  state.dueDiligenceCalendar

export const selectDownloadUrl = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.downloadUrl,
  )

export const selectEndTime = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.endTime,
  )

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectEvents = () =>
  createSelector(
    selectModuleState(),
    (mState) => {
      return mState.events
    },
  )

export const selectIsLoading = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoading,
  )

export const selectScope = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.scope,
  )

export const selectStartTime = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.startTime,
  )
